import React, { useRef } from "react";
import { Formik } from "formik";
import { Form } from "react-bootstrap";
import { EditorField, FormCollapsible, Select, SubmitButton, TextInput } from "components/forms/index";
import * as notifications from "notifications";
import MultipleFileDropZone from "../MultipleFileDropZone";

const SendEmailForm = ({
  onSubmitAction,
  emailTemplates,
  getSelectedEmails,
  loginInfo,
  selectedIds,
  templateType,
  title,
  mail = null,
}) => {
  const formikRef = useRef();
  const dropzoneRef = useRef();

  // Sort templates by name
  emailTemplates = emailTemplates.sort((a, b) => a.name.localeCompare(b.name));

  const handleEmailTemplateChange = e => {
    if (!e) return;
    let templateName = e.value;
    formikRef.current.setFieldValue("email_template", templateName);
    let body = prepareBody(templateName);
    formikRef.current.setFieldValue("subject", templateName);
    formikRef.current.setFieldValue("body", body);
    dropzoneRef.current.removeAll();
  };

  const handleToChange = email_source => {
    if (email_source) {
      formikRef.current.setFieldValue("_to", email_source.label);
      formikRef.current.setFieldValue("recipients", getSelectedEmails(email_source.label));
    }
  };

  const prepareBody = templateName => {
    let templateData = emailTemplates.find(item => item.name === templateName);
    if (templateData) {
      let body = templateData.html;
      let emailValues = {
        salesRepName: loginInfo.user_name,
        salesRepTitle: loginInfo.user_title,
        salesRepLicense: loginInfo.user_license,
        salesRepCalendlyLink: loginInfo.user_calendly_link,
        salesRepOfficePhone: loginInfo.user_phone,
        salesRepFax: loginInfo.user_fax,
        salesRepEmail: loginInfo.email,
      };
      body = body.replace("{Footer}", emailFooter(emailValues));
      return body;
    } else {
      return "";
    }
  };

  let initialTemplate = emailTemplates?.[0]?.name || "";
  let formattedFromEmail = `${loginInfo.user_name} <${loginInfo.email}>`;

  const initialRecipients = () => {
    if (mail) return mail;
    return templateType === "Accounts" ? getSelectedEmails("Work Email") : getSelectedEmails();
  };

  const initialTo = () => {
    if (mail) return mail;
    return templateType === "Accounts" ? "Work Email" : "";
  };

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          recipients: initialRecipients(),
          cc: "",
          email_template: initialTemplate,
          subject: initialTemplate,
          body: prepareBody(initialTemplate),
          _from: formattedFromEmail,
          _to: initialTo(),
        }}
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          try {
            let payload = {
              ...values,
              selected_ids: selectedIds,
              email_template_name: values.email_template,
            };
            const response = await onSubmitAction(payload);
            if (response && response.status !== 200) {
              notifications.error(`Error sending email to ${templateType}`);
              const data = response.data;
              if (data && data.error) {
                notifications.error(data.error);
              }
              setSubmitting(false);
              return false;
            }

            notifications.success(`Email sent to ${templateType} successfully`);
          } catch (error) {
            setErrors(error.response.data);
            notifications.error(`Error sending email to ${templateType}`);
          }
          setSubmitting(false);
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          const findCurrentTemplateFiles = () => {
            if (values.email_template) {
              const currentTemplate = emailTemplates.find(e => e.name === values.email_template);
              if (currentTemplate) {
                return [currentTemplate.file];
              }
            }
            return null;
          };

          return (
            <>
              <FormCollapsible onSubmit={handleSubmit} title={title}>
                <Form.Row>
                  <Select
                    label="Email Template"
                    name="email_template"
                    placeholder="Select Email Template"
                    onChange={handleEmailTemplateChange}
                    options={emailTemplates.map(template => ({ value: template.name, label: template.name }))}
                  />
                </Form.Row>
                <Form.Row>
                  <Select
                    label="From"
                    name="_from"
                    placeholder="Select sender email"
                    options={[{ value: formattedFromEmail, label: formattedFromEmail }]}
                  />
                  {templateType === "Accounts" && (
                    <Select
                      label="To"
                      name="_to"
                      placeholder="Select receiver email options"
                      options={[
                        { value: "Work Email", label: "Work Email" },
                        { value: "Case Status Email", label: "Case Status Email" },
                        { value: "Other Email", label: "Other Email" },
                        { value: "Other Email 2", label: "Other Email 2" },
                      ]}
                      onChange={handleToChange}
                      disabled={mail}
                    />
                  )}
                </Form.Row>
                <Form.Row>
                  <TextInput label="Recipients" name="recipients" as="textarea" rows={3} disabled={true} />
                </Form.Row>
                <Form.Row>
                  <TextInput label="CC" name="cc" placeholder="Enter CC Email(s)" />
                </Form.Row>
                <Form.Row>
                  <TextInput label="Subject" name="subject" />
                </Form.Row>
                <Form.Row>
                  <EditorField label="Email Template" name="body" />
                </Form.Row>
                <MultipleFileDropZone
                  setFieldValue={setFieldValue}
                  initialFiles={findCurrentTemplateFiles()}
                  ref={dropzoneRef}
                />
                <SubmitButton
                  defaultText={"Send"}
                  submittingText="Sending..."
                  style={{
                    width: "93%",
                    marginTop: "1rem",
                    marginBottom: "1rem",
                  }}
                />
              </FormCollapsible>
            </>
          );
        }}
      </Formik>
    </>
  );
};

const emailFooter = values => {
  let logoUrl = "https://api.liferocanalytics.com/static/img/liferoc-capital-logo.png";

  let {
    salesRepName,
    salesRepTitle,
    salesRepLicense,
    salesRepCalendlyLink,
    salesRepOfficePhone,
    salesRepFax,
    salesRepEmail,
  } = values;

  return `
  <p>
    <span style="text-align: -webkit-match-parent;">
      <b>${salesRepName}</b><br>
      ${salesRepLicense ? `${salesRepLicense}<br>` : ""}
      ${salesRepTitle ? `<i>${salesRepTitle}</i><br>` : ""}
      Call/Text: ${salesRepOfficePhone} ${salesRepFax ? `| Fax: ${salesRepFax}` : ""}<br>
      E: ${salesRepEmail}<br>
    </span>
    <b>19000 MacArthur Boulevard, Suite 450, Irvine, CA 92612<br></b>
    LifeRoc Capital, LLC (CA Life Settlement Provider Lic. #6256-2)<br>
    LifeRoc Financial & Insurance Services, LLC (CA Producer Lic. #0M73829)
  </p>

  <div className="se-component se-image-container __se__float-" contentEditable="false">
    <figure style="margin: 0px; width: 270px;">
      <img
        src="${logoUrl}"
        style="width: 270px; height: 54px;"
        alt="" data-proportion="true" data-align="" data-index="0" data-file-name="liferoc-capital-logo.png" data-file-size="0" data-origin="270px,54px" data-size="270px,54px"
      >
    </figure>
  </div>

  <p>
  ${
    salesRepCalendlyLink
      ? `<a href="${salesRepCalendlyLink}" target="_blank" rel="noreferrer" style="color: rgb(17, 85, 204);">Schedule a Call</a>&nbsp;|&nbsp;`
      : ""
  }<a href="http://www.liferoccapital.com/" target="_blank" rel="noreferrer" style="color: rgb(17, 85, 204);">www.LifeRocCapital.com</a>&nbsp;|&nbsp;
  <a href="https://www.linkedin.com/company/liferoc-capital-llc" target="_blank" rel="noreferrer" style="color: rgb(17, 85, 204);">Follow Us On LinkedIn</a>
  <br>
    _____________________________________________</p>

  <p><b style="font-size: 14px;">NOTICES &amp; DISCLOSURES</b><span style="font-size: 14px;"><b><br></b></span>
    <div style="font-weight: bold; text-align: left;">
    <span style="font-size: 8pt; text-align: justify; font-weight: 400;">This electronic mail communication, including all files transmitted with or attached to it (together, this “message”), may contain non-public, privileged, protected, disclosure-restricted, and/or confidential information, and is intended solely for use by the intended recipient for the purpose of providing service to LifeRoc Capital, LLC or LifeRoc Financial &amp; Insurance Services, LLC (“LifeRoc”) for which the intended recipient receives this message.&nbsp; If you are not the intended recipient of this message, you are strictly prohibited from disclosing, copying, distributing, or using any content contained in, transmitted with, or attached to it.&nbsp; If you received this message in error, please contact &amp; inform us immediately and destroy the message in its entirety in all forms.&nbsp; Receipt of this message by anyone other than the intended recipient is not a waiver of confidentiality or privilege of any of its content. Views and opinions expressed in this message are those of the sender, do not necessarily express views and opinions of LifeRoc, are not intended to constitute a “reliance opinion” under applicable Treasury Regulations, and are not intended or written to be used, and may not be used or relied upon, for the purpose of (i) avoiding tax-related penalties that may imposed by the United States Internal Revenue Service, or (ii) promoting, marketing, or recommending to another party any tax-related matters addressed in this message. LifeRoc and its officers, directors, employees, agents, and representatives do not provide legal, tax, accounting, investment, estate, or financial planning advice or guidance.&nbsp; This message is not intended to provide, and should not be relied on for, any legal, tax, accounting, investment, estate, or financial planning advice or guidance.&nbsp; You should consult your own independent legal, tax, accounting, investment, estate, and financial planning advisors before engaging in any transaction which may be described in this message. Recognizing the inevitability of e-mail messages crossing into various jurisdictions without LifeRoc’s intent and/or knowledge, please note that LifeRoc currently does not accept proposed viatical and/or life settlement transactions subject to regulation under the laws of states which do not yet permit it to conduct such (whether due to LifeRoc not yet having obtained requisite licensure from the corresponding state insurance regulatory authority, the lack of a corresponding &amp; applicable licensure exemption, or otherwise).&nbsp; LifeRoc only accepts for review and evaluation client transactions involving potential/current policy owners in states where LifeRoc is duly licensed.</span>
    </div>
  </p>`;
};

export default SendEmailForm;
